import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

/**
 * UserActivityEvent API
 * 
 * This API connects to the backend's user activity event tracking system.
 * It provides endpoints for:
 * - Admin: View any user's timeline, search events, get stats
 * - Users: View their own activity timeline and billing history
 * 
 * Categories tracked:
 * 1. Authentication - logins, password resets, email verification
 * 2. Billing - subscriptions, payments, refunds, disputes (from Stripe)
 * 3. Access/Features - CRM access, add-ons, feature permissions
 * 4. Communications - SMS/Email sent, delivered, failed, opt-outs
 * 5. Admin Actions - plan changes, suspensions, credits, data edits
 * 6. Compliance - A2P campaigns, business lines, quiet hours, consent
 */

export const userActivityEventApi = createApi({
  reducerPath: "userActivityEventApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["UserActivityEvent", "ActivityStats"],
  endpoints: (builder) => ({
    // ============================================
    // ADMIN ENDPOINTS
    // ============================================

    /**
     * Get activity stats for admin dashboard
     * Shows counts by category, failed payments, signups, cancellations
     */
    /**
     * Who on the team has been doing what — the record trail an owner sees
     * on the Team page. Owner-scoped by the server.
     */
    getTeamActivity: builder.query({
      query: ({ limit = 50, actorId } = {}) => ({
        url: "/user-activity/team",
        params: { limit, ...(actorId ? { actorId } : {}) },
      }),
      transformResponse: (r) => r?.data || { events: [], byPerson: [] },
      providesTags: ["UserActivityEvent"],
    }),

    getAdminActivityStats: builder.query({
      query: ({ days = 7 } = {}) => ({
        url: "/user-activity/admin/stats",
        params: { days },
      }),
      providesTags: ["ActivityStats"],
    }),

    /**
     * Search events across all users (admin only)
     * Filter by eventType, source, category, date range
     */
    searchActivityEvents: builder.query({
      query: ({ eventType, source, category, stripeCustomerId, startDate, endDate, limit = 100 } = {}) => ({
        url: "/user-activity/admin/search",
        params: { eventType, source, category, stripeCustomerId, startDate, endDate, limit },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get a specific user's activity timeline (admin only)
     */
    getAdminUserTimeline: builder.query({
      query: ({ userId, category, limit = 100, page = 1 } = {}) => ({
        url: `/user-activity/admin/${userId}`,
        params: { category, limit, page },
      }),
      providesTags: (result, error, { userId }) => [
        { type: "UserActivityEvent", id: userId },
      ],
    }),

    /**
     * Get all events by category for admin dashboard views
     */
    getEventsByCategory: builder.query({
      query: ({ category, limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { category, limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get all billing events for payments overview
     */
    getBillingEvents: builder.query({
      query: ({ eventType, limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { category: "billing", eventType, limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get communication events for messaging overview
     */
    getCommunicationEvents: builder.query({
      query: ({ eventType, limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { category: "communication", eventType, limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get admin action events
     */
    getAdminActionEvents: builder.query({
      query: ({ limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { category: "admin", limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get compliance events
     */
    getComplianceEvents: builder.query({
      query: ({ limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { category: "compliance", limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get failed payments specifically
     */
    getFailedPayments: builder.query({
      query: ({ limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { eventType: "payment_failed", limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get subscription changes (upgrades, downgrades, cancellations)
     */
    getSubscriptionChanges: builder.query({
      query: ({ limit = 100, startDate, endDate } = {}) => ({
        url: "/user-activity/admin/search",
        params: { category: "billing", limit, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    // ============================================
    // USER ENDPOINTS
    // ============================================

    /**
     * Get current user's activity timeline
     */
    getMyTimeline: builder.query({
      query: ({ category, limit = 50, page = 1, startDate, endDate } = {}) => ({
        url: "/user-activity",
        params: { category, limit, page, startDate, endDate },
      }),
      providesTags: ["UserActivityEvent"],
    }),

    /**
     * Get current user's billing history
     */
    getMyBillingHistory: builder.query({
      query: () => "/user-activity/billing",
      providesTags: ["UserActivityEvent"],
    }),
  }),
});

export const {
  useGetTeamActivityQuery,
  // Admin hooks
  useGetAdminActivityStatsQuery,
  useSearchActivityEventsQuery,
  useGetAdminUserTimelineQuery,
  useGetEventsByCategoryQuery,
  useGetBillingEventsQuery,
  useGetCommunicationEventsQuery,
  useGetAdminActionEventsQuery,
  useGetComplianceEventsQuery,
  useGetFailedPaymentsQuery,
  useGetSubscriptionChangesQuery,
  // User hooks
  useGetMyTimelineQuery,
  useGetMyBillingHistoryQuery,
} = userActivityEventApi;

// Export event type constants for filtering
export const EVENT_CATEGORIES = {
  AUTHENTICATION: "authentication",
  BILLING: "billing",
  ACCESS: "access",
  COMMUNICATION: "communication",
  ADMIN: "admin",
  COMPLIANCE: "compliance",
};

export const EVENT_TYPES = {
  // Authentication
  LOGIN_SUCCESS: "login_success",
  PASSWORD_RESET: "password_reset",
  EMAIL_VERIFIED: "email_verified",
  
  // Billing
  SUBSCRIPTION_CREATED: "subscription_created",
  SUBSCRIPTION_UPGRADED: "subscription_upgraded",
  SUBSCRIPTION_DOWNGRADED: "subscription_downgraded",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  PAYMENT_SUCCEEDED: "payment_succeeded",
  PAYMENT_FAILED: "payment_failed",
  INVOICE_GENERATED: "invoice_generated",
  REFUND_ISSUED: "refund_issued",
  DISPUTE_OPENED: "dispute_opened",
  DISPUTE_CLOSED: "dispute_closed",
  
  // Access
  CRM_CREATED: "crm_created",
  CRM_UNLOCKED: "crm_unlocked",
  CRM_LOCKED: "crm_locked",
  ADDON_ENABLED: "addon_enabled",
  ADDON_DISABLED: "addon_disabled",
  FEATURE_ACCESS_GRANTED: "feature_access_granted",
  FEATURE_ACCESS_BLOCKED: "feature_access_blocked",
  
  // Communication
  SMS_SENT: "sms_sent",
  SMS_DELIVERED: "sms_delivered",
  SMS_FAILED: "sms_failed",
  EMAIL_SENT: "email_sent",
  EMAIL_DELIVERED: "email_delivered",
  EMAIL_BOUNCED: "email_bounced",
  EMAIL_FAILED: "email_failed",
  OPT_OUT: "opt_out",
  UNSUBSCRIBE: "unsubscribe",
  
  // Admin
  ADMIN_PLAN_CHANGE: "admin_plan_change",
  ADMIN_ACCOUNT_SUSPENDED: "admin_account_suspended",
  ADMIN_ACCOUNT_REACTIVATED: "admin_account_reactivated",
  ADMIN_DATA_EDIT: "admin_data_edit",
  ADMIN_CREDIT_APPLIED: "admin_credit_applied",
  ADMIN_OVERRIDE: "admin_override",
  
  // Compliance
  A2P_CAMPAIGN_LINKED: "a2p_campaign_linked",
  BUSINESS_LINE_PROVISIONED: "business_line_provisioned",
  QUIET_HOURS_ENFORCED: "quiet_hours_enforced",
  CONSENT_RECORDED: "consent_recorded",
};

export const EVENT_SOURCES = {
  AUTH: "auth",
  STRIPE: "stripe",
  TWILIO: "twilio",
  SENDGRID: "sendgrid",
  ADMIN: "admin",
  SYSTEM: "system",
  API: "api",
};
